// Browser-compatible Device Manager for ACCTOO
// Handles multi-device registration and management

import BrowserSQLiteManager from './BrowserSQLiteManager.js';

class BrowserDeviceManager {
  constructor(sqliteManager) {
    this.sqliteManager = sqliteManager;
  }

  // =============================================
  // DEVICE ID GENERATION
  // =============================================

  // Generate stable device ID
  generateDeviceId(deviceType = 'owner', counterId = '') {
    try {
      // Get browser fingerprint
      const browserFingerprint = this.getBrowserFingerprint();
      
      // Generate timestamp
      const timestamp = Date.now().toString();
      
      // Create unique identifier
      const uniqueId = `${browserFingerprint}_${timestamp}_${deviceType}_${counterId}`;
      
      // Generate hash using Web Crypto API
      const encoder = new TextEncoder();
      const data = encoder.encode(uniqueId);
      
      return crypto.subtle.digest('SHA-256', data).then(hashBuffer => {
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        
        const deviceId = `STABLE_${hashHex.substring(0, 16)}_${hashHex.substring(16, 24)}`;
        
        console.log('✅ Device ID generated:', deviceId);
        return deviceId;
      });
    } catch (error) {
      console.error('❌ Device ID generation failed:', error);
      // Fallback to simple random ID
      const randomId = Math.random().toString(36).substring(2, 15);
      return `STABLE_${randomId}_${Date.now().toString(36)}`;
    }
  }

  // Get browser fingerprint
  getBrowserFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
      
      const fingerprint = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screen: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        canvas: canvas.toDataURL(),
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        maxTouchPoints: navigator.maxTouchPoints || 0,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack
      };
      
      return JSON.stringify(fingerprint);
    } catch (error) {
      console.warn('⚠️ Could not get browser fingerprint:', error);
      return navigator.userAgent + navigator.language + window.screen.width + window.screen.height;
    }
  }

  // =============================================
  // DEVICE REGISTRATION
  // =============================================

  // Register owner device
  async registerOwnerDevice(userId, companyId, deviceName = 'Main Counter') {
    try {
      const deviceId = await this.generateDeviceId('owner');
      const deviceData = {
        id: `device_${Date.now()}`,
        deviceId: deviceId,
        companyId: companyId,
        deviceType: 'owner',
        deviceName: deviceName,
        billPrefix: 'INV',
        billCounter: '',
        status: 'active'
      };

      await this.sqliteManager.insert('devices', deviceData);
      await this.sqliteManager.setupBillSequences(companyId, deviceId);
      
      console.log('✅ Owner device registered:', deviceId);
      return { success: true, deviceId, deviceData };
    } catch (error) {
      console.error('❌ Owner device registration failed:', error);
      throw error;
    }
  }

  // Register counter device
  async registerCounterDevice(companyId, deviceName, counterId) {
    try {
      const deviceId = await this.generateDeviceId('counter', counterId);
      const deviceData = {
        id: `device_${Date.now()}`,
        deviceId: deviceId,
        companyId: companyId,
        deviceType: 'counter',
        deviceName: deviceName,
        billPrefix: 'INV',
        billCounter: counterId,
        status: 'pending' // Requires owner approval
      };

      await this.sqliteManager.insert('devices', deviceData);
      await this.sqliteManager.setupBillSequences(companyId, deviceId, counterId);
      
      console.log('✅ Counter device registered:', deviceId);
      return { success: true, deviceId, deviceData };
    } catch (error) {
      console.error('❌ Counter device registration failed:', error);
      throw error;
    }
  }

  // Register mobile device
  async registerMobileDevice(companyId, deviceName) {
    try {
      const deviceId = await this.generateDeviceId('mobile');
      const deviceData = {
        id: `device_${Date.now()}`,
        deviceId: deviceId,
        companyId: companyId,
        deviceType: 'mobile',
        deviceName: deviceName,
        billPrefix: 'INV',
        billCounter: 'MOB',
        status: 'pending' // Requires owner approval
      };

      await this.sqliteManager.insert('devices', deviceData);
      await this.sqliteManager.setupBillSequences(companyId, deviceId, 'MOB');
      
      console.log('✅ Mobile device registered:', deviceId);
      return { success: true, deviceId, deviceData };
    } catch (error) {
      console.error('❌ Mobile device registration failed:', error);
      throw error;
    }
  }

  // =============================================
  // DEVICE MANAGEMENT
  // =============================================

  // Get all devices for company
  async getCompanyDevices(companyId) {
    try {
      const devices = await this.sqliteManager.select('devices', { company_id: companyId });
      console.log('✅ Company devices retrieved:', devices.length);
      return devices;
    } catch (error) {
      console.error('❌ Get company devices failed:', error);
      throw error;
    }
  }

  // Get device by ID
  async getDevice(deviceId) {
    try {
      const devices = await this.sqliteManager.select('devices', { device_id: deviceId });
      if (devices.length === 0) {
        throw new Error('Device not found');
      }
      return devices[0];
    } catch (error) {
      console.error('❌ Get device failed:', error);
      throw error;
    }
  }

  // Update device status
  async updateDeviceStatus(deviceId, status) {
    try {
      await this.sqliteManager.update('devices', deviceId, { status });
      console.log('✅ Device status updated:', deviceId, status);
      return { success: true };
    } catch (error) {
      console.error('❌ Update device status failed:', error);
      throw error;
    }
  }

  // Approve device
  async approveDevice(deviceId) {
    try {
      await this.updateDeviceStatus(deviceId, 'active');
      console.log('✅ Device approved:', deviceId);
      return { success: true };
    } catch (error) {
      console.error('❌ Approve device failed:', error);
      throw error;
    }
  }

  // Reject device
  async rejectDevice(deviceId) {
    try {
      await this.updateDeviceStatus(deviceId, 'rejected');
      console.log('✅ Device rejected:', deviceId);
      return { success: true };
    } catch (error) {
      console.error('❌ Reject device failed:', error);
      throw error;
    }
  }

  // Deactivate device
  async deactivateDevice(deviceId) {
    try {
      await this.updateDeviceStatus(deviceId, 'inactive');
      console.log('✅ Device deactivated:', deviceId);
      return { success: true };
    } catch (error) {
      console.error('❌ Deactivate device failed:', error);
      throw error;
    }
  }

  // =============================================
  // BILL NUMBER MANAGEMENT
  // =============================================

  // Get next bill number for device
  async getNextBillNumber(deviceId, billType) {
    try {
      const device = await this.getDevice(deviceId);
      const billNumber = await this.sqliteManager.generateBillNumber(billType);
      
      console.log('✅ Next bill number generated:', billNumber);
      return billNumber;
    } catch (error) {
      console.error('❌ Get next bill number failed:', error);
      throw error;
    }
  }

  // Get bill sequences for device
  async getBillSequences(deviceId) {
    try {
      const device = await this.getDevice(deviceId);
      const sequences = await this.sqliteManager.select('bill_sequences', { 
        company_id: device.company_id, 
        device_id: deviceId 
      });
      
      console.log('✅ Bill sequences retrieved:', sequences.length);
      return sequences;
    } catch (error) {
      console.error('❌ Get bill sequences failed:', error);
      throw error;
    }
  }

  // =============================================
  // DEVICE STATISTICS
  // =============================================

  // Get device statistics
  async getDeviceStats(companyId) {
    try {
      const devices = await this.getCompanyDevices(companyId);
      
      const stats = {
        total: devices.length,
        active: devices.filter(d => d.status === 'active').length,
        pending: devices.filter(d => d.status === 'pending').length,
        inactive: devices.filter(d => d.status === 'inactive').length,
        byType: {
          owner: devices.filter(d => d.device_type === 'owner').length,
          counter: devices.filter(d => d.device_type === 'counter').length,
          mobile: devices.filter(d => d.device_type === 'mobile').length
        }
      };
      
      console.log('✅ Device statistics retrieved:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Get device statistics failed:', error);
      throw error;
    }
  }

  // =============================================
  // DEVICE VALIDATION
  // =============================================

  // Validate device ID format
  validateDeviceId(deviceId) {
    try {
      const pattern = /^STABLE_[a-f0-9]{16}_[a-f0-9]{8}$/;
      return pattern.test(deviceId);
    } catch (error) {
      console.error('❌ Validate device ID failed:', error);
      return false;
    }
  }

  // Check if device is authorized
  async isDeviceAuthorized(deviceId) {
    try {
      const device = await this.getDevice(deviceId);
      return device.status === 'active';
    } catch (error) {
      console.error('❌ Check device authorization failed:', error);
      return false;
    }
  }

  // =============================================
  // DEVICE CLEANUP
  // =============================================

  // Remove old inactive devices
  async cleanupInactiveDevices(companyId, daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);
      
      const devices = await this.getCompanyDevices(companyId);
      const oldDevices = devices.filter(device => 
        device.status === 'inactive' && 
        new Date(device.updated_at) < cutoffDate
      );
      
      for (const device of oldDevices) {
        await this.removeDevice(device.device_id);
      }
      
      console.log('✅ Inactive devices cleaned up:', oldDevices.length);
      return { success: true, removed: oldDevices.length };
    } catch (error) {
      console.error('❌ Cleanup inactive devices failed:', error);
      throw error;
    }
  }

  // Remove device and all its data
  async removeDevice(deviceId) {
    try {
      // Remove device
      await this.sqliteManager.delete('devices', deviceId);
      
      // Remove bill sequences
      const sequences = await this.sqliteManager.select('bill_sequences', { device_id: deviceId });
      for (const sequence of sequences) {
        await this.sqliteManager.delete('bill_sequences', sequence.id);
      }
      
      // Remove sync queue entries
      const syncRecords = await this.sqliteManager.select('sync_queue', { device_id: deviceId });
      for (const record of syncRecords) {
        await this.sqliteManager.delete('sync_queue', record.id);
      }
      
      console.log('✅ Device removed:', deviceId);
      return { success: true };
    } catch (error) {
      console.error('❌ Remove device failed:', error);
      throw error;
    }
  }
}

export default BrowserDeviceManager;
